package org.teddapp.project.ui.skills

import androidx.compose.foundation.background
import org.teddapp.project.ui.TeddFitText
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.selection.DisableSelection
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Skill
import androidx.compose.ui.text.style.TextOverflow
import com.tedd.db.Task
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddCard
import org.teddapp.project.ui.TeddDateField
import org.teddapp.project.ui.TeddDates
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTier
import org.teddapp.project.ui.TeddTierBadge
import org.teddapp.project.ui.TeddTokenProgress
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.nav.NavIntent
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.tierColor
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.teddClickable
import androidx.compose.foundation.border

/** Heures lisibles : « 22,3 h », « 45 min » sous l'heure. */
internal fun formatHours(minutes: Int): String = when {
    minutes < 60 -> "$minutes min"
    else -> {
        val h = minutes / 60
        val tenths = ((minutes % 60) * 10) / 60
        if (tenths == 0) "$h h" else "$h,$tenths h"
    }
}

/**
 * Compétences : ce que j'apprends et où j'en suis. Liste à gauche, détail à droite en étendu ;
 * pile en compact. Les heures viennent des courses de focus, les paliers de Kaufman.
 */
@Composable
internal fun SkillsView(
    model: SkillsModel,
    compact: Boolean,
    modifier: Modifier = Modifier,
    onNavigate: (Section) -> Unit = {},
    onStartFocus: ((task: Task, minutes: Int) -> Unit)? = null,
) {
    val stats = model.stats
    // L'assistant d'une compétence prend toute la place de l'écran des compétences.
    model.assistantFor?.let { id -> stats.firstOrNull { it.skill.id == id } }?.let { st ->
        AssistantScreen(
            repos = model.repositories, skillId = st.skill.id, skillName = st.skill.name, compact = compact,
            onClose = { model.assistantFor = null }, modifier = modifier,
        )
        return
    }
    if (stats.isEmpty()) {
        SkillsEmpty(onCreate = { model.creating = true }, onLoad = { ParcoursLoadButton(model, size = TeddButtonSize.Md, kind = TeddButtonKind.Secondary) }, modifier = modifier)
    } else if (compact) {
        Column(
            modifier = modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(horizontal = 16.dp, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            SkillsHeaderRow(model, onNavigate)
            if (model.overview) {
                SelectionContainer {
                    SkillsOverview(model, onSelect = { id -> model.selectedId = id; model.overview = false })
                }
            } else {
                model.byFamily(model.visible).forEach { (famille, membres) ->
                    FamilyHeader(famille, membres.size)
                    membres.forEach { st ->
                        SkillCard(st, selected = st.skill.id == model.selected?.skill?.id) {
                            model.selectedId = if (model.selectedId == st.skill.id) null else st.skill.id
                        }
                        if (model.selectedId == st.skill.id) SkillDetail(st, model, compact = true, onNavigate = onNavigate, onStartFocus = onStartFocus)
                    }
                }
                if (model.visible.isEmpty()) SkillsNoMatch(model)
                ArchivedSection(model) { id -> model.selectedId = if (model.selectedId == id) null else id }
            }
            UnclassifiedNote(model)
        }
    } else {
        org.teddapp.project.ui.RecompositionProbe("skills.expanded")
        Row(modifier = modifier.fillMaxSize()) {
            org.teddapp.project.ui.TeddSidePane(
                spec = SKILLS_PANE,
                title = "la liste des compétences",
                collapsedContent = { expand ->
                    org.teddapp.project.ui.TeddPaneRail(
                        expand = expand,
                        title = "la liste des compétences",
                        items = model.visible.map { st ->
                            org.teddapp.project.ui.PaneRailItem(
                                key = st.skill.id, label = st.skill.name, icon = st.skill.icon,
                                selected = st.skill.id == model.selected?.skill?.id,
                                color = runCatching { Color(("FF" + st.skill.color.trimStart('#')).toLong(16)) }.getOrNull(),
                                onClick = { model.selectedId = st.skill.id },
                            )
                        },
                    )
                },
            ) {
                Column(
                    modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    org.teddapp.project.ui.RecompositionProbe("skills.list")
                    SkillsHeaderRow(model, onNavigate)
                    model.byFamily(model.visible).forEach { (famille, membres) ->
                        FamilyHeader(famille, membres.size)
                        membres.forEach { st ->
                            SkillCard(st, selected = st.skill.id == model.selected?.skill?.id) { model.selectedId = st.skill.id }
                        }
                    }
                    if (model.visible.isEmpty()) SkillsNoMatch(model)
                    ArchivedSection(model) { id -> model.selectedId = id }
                    UnclassifiedNote(model)
                }
            }
            Box(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight()
                    .verticalScroll(rememberScrollState())
                    .padding(start = 24.dp, end = 40.dp, top = 28.dp, bottom = 48.dp),
            ) {
                // La fiche prend toute la largeur disponible : sur un grand écran, les
                // modules et les chiffres respirent au lieu de laisser un tiers de vide.
                Box(modifier = Modifier.fillMaxWidth()) {
                    org.teddapp.project.ui.RecompositionProbe("skills.detail")
                    if (model.overview) {
                        SelectionContainer {
                            SkillsOverview(model, onSelect = { id -> model.selectedId = id; model.overview = false })
                        }
                    } else {
                        model.selected?.let { SkillDetail(it, model, compact = false, onNavigate = onNavigate, onStartFocus = onStartFocus) }
                    }
                }
            }
        }
    }
    if (model.creating || model.editing != null) {
        SkillEditor(
            existing = model.editing,
            families = model.allFamilies,
            onSave = { name, intention, color, icon, target, tags, deadline ->
                model.save(model.editing, name, intention, color, icon, target, tags, deadline)
            },
            onDelete = model.editing?.let { sk -> { model.delete(sk) } },
            onClose = { model.creating = false; model.editing = null },
        )
    }
    model.pendingImport?.let { plan ->
        ParcoursImportDialog(plan, model, currentSkillName = model.selected?.skill?.name, currentSkillId = model.selected?.skill?.id)
    }
    if (model.importErrors.isNotEmpty()) ParcoursErrorsDialog(model.importErrors) { model.importErrors = emptyList() }
    if (model.pasteOpen) ParcoursPasteDialog(model)
}

@Composable
private fun SkillsHeaderRow(model: SkillsModel, onNavigate: (Section) -> Unit) {
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3), modifier = Modifier.fillMaxWidth()) {
        // Le titre sur sa ligne, les boutons sur la leur : dans une colonne étroite, les
        // trois côte à côte coupaient le titre en deux.
        val filtered = model.tagFilter != null || model.search.isNotBlank()
        Column {
            Text("Mes compétences", style = TeddType.BodyStrong, color = TeddTokens.Ink, maxLines = 1)
            Text(
                text = if (filtered) "${model.visible.size} sur ${model.inPlayCount}" else "${model.visible.size} en cours",
                style = TeddType.MonoSm.copy(fontSize = 11.sp),
                color = TeddTokens.InkMuted,
            )
        }
        Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S1)) {
            TeddButton(
                text = if (model.overview) "Liste" else "Aperçu",
                onClick = { model.overview = !model.overview },
                kind = TeddButtonKind.Ghost,
                size = TeddButtonSize.Sm,
            )
            TeddButton(text = "Nouvelle", onClick = { model.creating = true }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "plus")
        }
        SkillsSearchField(model)
        SkillsTagRow(model)
        // Le recueil (ce que je sais faire, auto-évalué) et le portfolio vivent à côté : on y mène d'ici.
        TeddButton(
            text = "Recueil et portfolio",
            onClick = { onNavigate(Section.Portfolio) },
            kind = TeddButtonKind.Ghost,
            size = TeddButtonSize.Sm,
            icon = "arrow-right",
        )
    }
}

/**
 * Les étiquettes en usage, les plus portées d'abord.
 *
 * Il n'y a pas de liste d'étiquettes à gérer : elles naissent quand une compétence
 * les porte et disparaissent quand la dernière les lâche. Une de moins à entretenir.
 */
@Composable
private fun SkillsTagRow(model: SkillsModel) {
    val tags = model.allTags
    if (tags.isEmpty()) return
    FlowRow(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        // « Toutes » lève le filtre : sans elle, on croirait la liste incomplète.
        org.teddapp.project.ui.TeddFilterChip(label = "Toutes", selected = model.tagFilter == null, onClick = { model.tagFilter = null }, count = model.inPlayCount)
        tags.forEach { tag ->
            val on = model.tagFilter == tag
            org.teddapp.project.ui.TeddFilterChip(label = tag, selected = on, onClick = { model.tagFilter = if (on) null else tag }, count = model.tagCount(tag))
        }
    }
}

/** Cherche dans le nom et l'intention : les deux choses qu'on retient d'une compétence. */
@Composable
private fun SkillsSearchField(model: SkillsModel) {
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(40.dp)
            .background(TeddTokens.Surface, shape)
            .border(1.dp, TeddTokens.LineStrong, shape)
            .padding(horizontal = TeddSpace.S3),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
    ) {
        TeddIcon(name = "search", size = 14.dp, color = TeddTokens.InkMuted)
        BasicTextField(
            value = model.search,
            onValueChange = { model.search = it },
            singleLine = true,
            textStyle = TeddType.Body.copy(color = TeddTokens.Ink),
            cursorBrush = SolidColor(TeddTokens.Ink),
            modifier = Modifier.weight(1f),
            decorationBox = { inner ->
                if (model.search.isEmpty()) Text("Chercher une compétence", color = TeddTokens.InkSubtle, style = TeddType.Body)
                inner()
            },
        )
    }
}

/**
 * Les compétences mises de côté, repliées.
 *
 * Archiver n'efface rien : les heures restent comptées et la compétence se retrouve.
 * C'est ce qui permet de déclarer une ambition sans encombrer la liste de tous les
 * jours.
 */
@Composable
private fun ArchivedSection(model: SkillsModel, onSelect: (String) -> Unit) {
    if (model.archivedCount == 0) return
    Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.padding(top = 10.dp)) {
        TeddButton(
            text = if (model.showArchived) "Masquer les archivées" else "Archivées · ${model.archivedCount}",
            onClick = { model.showArchived = !model.showArchived },
            kind = TeddButtonKind.Ghost,
            size = TeddButtonSize.Sm,
        )
        if (model.showArchived) {
            model.archived.forEach { st ->
                SkillCard(st, selected = st.skill.id == model.selected?.skill?.id) { onSelect(st.skill.id) }
            }
        }
    }
}

/** Titre d'une famille dans la liste : le nom en petites capitales et le nombre de membres. */
@Composable
private fun FamilyHeader(famille: String, count: Int) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.fillMaxWidth().padding(top = 10.dp, bottom = 2.dp),
    ) {
        Text(famille.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.weight(1f))
        Text(count.toString(), style = TeddType.Mono, color = TeddTokens.InkSubtle, fontSize = 11.sp)
    }
}

@Composable
private fun SkillCard(st: SkillStats, selected: Boolean, onClick: () -> Unit) {
    val color = runCatching { Color(("FF" + st.skill.color.trimStart('#')).toLong(16)) }.getOrElse { TeddTokens.LimeStrong }
    val shape = RoundedCornerShape(TeddTokens.RLg)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            // Une carte par compétence ; celle qu'on lit est cerclée d'encre.
            .border(1.dp, if (selected) TeddTokens.Ink else TeddTokens.Line, shape)
            .teddClickable(onClick = onClick, shape = shape, fill = TeddTokens.Surface, hover = TeddTokens.Surface, role = null)
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            Box(
                modifier = Modifier.size(26.dp).clip(RoundedCornerShape(TeddTokens.RSm)).background(color.copy(alpha = 0.14f), RoundedCornerShape(TeddTokens.RSm)),
                contentAlignment = Alignment.Center,
            ) { TeddIcon(name = st.skill.icon, size = 13.dp, color = color, strokeWidth = 2.dp) }
            Text(st.skill.name, style = TeddType.Heading, color = TeddTokens.Ink, modifier = Modifier.weight(1f))
            // Un point suffit à dire « épinglée » : la position en tête le dit déjà.
            if (st.skill.pinned != 0L) TeddIcon(name = "pin", size = 14.dp, color = TeddTokens.InkMuted)
            st.tier?.let { TeddTierBadge(it) }
        }
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(formatHours(st.minutes), style = TeddType.Numeral.copy(fontSize = 20.sp, lineHeight = 24.sp), color = TeddTokens.Ink)
            TeddTokenProgress(
                value = st.tokensToTarget,
                accent = st.tier?.let { tierColor(it) } ?: TeddTokens.InkMuted,
                height = 10.dp,
                showLabel = false,
                modifier = Modifier.weight(1f),
            )
        }
        val remaining = st.hoursToNext
        val suivant = st.nextTier
        Text(
            text = when {
                remaining != null && suivant != null ->
                    "Encore ${formatHours((remaining * 60).toInt())} avant ${suivant.label.lowercase()}"
                st.tier != null -> "Palier le plus haut atteint."
                // Le palier vient du noyau : sans lui, on ne devine pas.
                else -> "Palier calculé dès que le noyau est actif."
            },
            color = TeddTokens.InkMuted,
            style = TeddType.Caption,
        )
    }
}

@Composable
private fun SkillDetail(
    st: SkillStats,
    model: SkillsModel,
    compact: Boolean,
    onNavigate: (Section) -> Unit = {},
    onStartFocus: ((Task, Int) -> Unit)? = null,
) {
    // La fiche se lit et s'étudie : tout son texte se sélectionne et se copie.
    // Les libellés d'action en sont exclus, pour ne pas les ramasser au passage.
    SelectionContainer {
        SkillDetailContent(st, model, compact, onNavigate, onStartFocus)
    }
}

@Composable
private fun SkillDetailContent(
    st: SkillStats,
    model: SkillsModel,
    compact: Boolean,
    onNavigate: (Section) -> Unit,
    onStartFocus: ((Task, Int) -> Unit)?,
) {
    Column(
        verticalArrangement = Arrangement.spacedBy(if (compact) 20.dp else 36.dp),
        modifier = Modifier.fillMaxWidth(),
    ) {
        // L'en-tête n'est pas une carte : c'est le haut d'une page. Une carte dans une
        // vue déjà cadrée ajoute un trait qui ne dit rien et resserre la lecture.
        Column(verticalArrangement = Arrangement.spacedBy(18.dp), modifier = Modifier.fillMaxWidth()) {
            // Les actions au-dessus, le titre sur toute la largeur : côte à côte, les boutons
            // prenaient la moitié de la place et le nom se coupait au milieu d'un mot.
            DisableSelection {
                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(4.dp, Alignment.End),
                    verticalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                TeddButton(
                    text = if (st.skill.pinned == 0L) "Épingler" else "Désépingler",
                    onClick = { model.togglePinned(st.skill) },
                    kind = TeddButtonKind.Ghost,
                    size = TeddButtonSize.Sm,
                )
                TeddButton(
                    text = if (st.skill.archived == 0L) "Archiver" else "Remettre en jeu",
                    onClick = { model.toggleArchived(st.skill) },
                    kind = TeddButtonKind.Ghost,
                    size = TeddButtonSize.Sm,
                )
                TeddButton(text = "Modifier", onClick = { model.editing = st.skill }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
                TeddButton(text = "Assistant", onClick = { model.assistantFor = st.skill.id }, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm, icon = "spark")
                val converted = model.mindMapsOf(st.skill.id).isNotEmpty()
                TeddButton(
                    text = if (converted) "Carte heuristique" else "Convertir en carte",
                    onClick = { model.openOrConvertMindMap(st.skill) { id -> NavIntent.openMindMap(id); onNavigate(Section.MindMaps) } },
                    kind = TeddButtonKind.Secondary,
                    size = TeddButtonSize.Sm,
                    icon = "mindmap",
                )
                // Reconvertir = mettre la carte à jour, jamais en créer une seconde.
                if (converted) TeddButton(
                    text = "Mettre à jour la carte",
                    onClick = { model.updateMindMap(st.skill) { id, _ -> NavIntent.openMindMap(id); onNavigate(Section.MindMaps) } },
                    kind = TeddButtonKind.Ghost,
                    size = TeddButtonSize.Sm,
                    icon = "refresh",
                )
                }
            }
            Column(modifier = Modifier.fillMaxWidth()) {
                TeddFitText(
                    text = st.skill.name,
                    style = if (compact) TeddType.Title1 else TeddType.DisplayLg,
                    minFontSize = if (compact) 18.sp else 24.sp,
                    maxLines = if (compact) 4 else 3,
                    modifier = Modifier.fillMaxWidth(),
                )
                st.skill.intention?.takeIf { it.isNotBlank() }?.let {
                    Text(
                        text = "« $it »",
                        style = TeddType.BodyLg,
                        color = TeddTokens.InkMuted,
                        fontSize = 16.sp,
                        modifier = Modifier.padding(top = 8.dp).widthIn(max = 520.dp),
                    )
                }
            }
            val etiquettes = model.tagsOf(st.skill)
            if (etiquettes.isNotEmpty()) {
                DisableSelection {
                    FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        etiquettes.forEach { tag ->
                            Box(
                                modifier = Modifier
                                    .clip(RoundedCornerShape(TeddTokens.RFull))
                                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
                                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                                        model.tagFilter = tag
                                    }
                                    .padding(horizontal = 10.dp, vertical = 5.dp),
                            ) {
                                Text(tag, color = TeddTokens.InkMuted, fontSize = 11.5.sp, fontWeight = FontWeight.SemiBold)
                            }
                        }
                    }
                }
            }
            Row(horizontalArrangement = Arrangement.spacedBy(if (compact) 26.dp else 48.dp)) {
                DetailNumber(if (st.fromCore) "temps investi · noyau" else "temps investi", formatHours(st.minutes), compact)
                DetailNumber("courses", st.sessions.size.toString(), compact)
                DetailNumber("explications", st.explanations.size.toString(), compact)
            }
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Row(modifier = Modifier.weight(1f), horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                        Text("VERS ${st.skill.target_hours} H", style = TeddType.Overline, color = TeddTokens.InkMuted)
                        SkillDeadline.label(st.skill.deadline)?.let { DeadlineText(it, overline = true) }
                    }
                    // Dire quand un jeton s'allume évite de croire la barre en panne.
                    Text(
                        "${formatHours((st.skill.target_hours * 6).toInt())} par jeton · courses et tâches barrées",
                        style = TeddType.Mono, color = TeddTokens.InkSubtle, fontSize = 11.sp,
                    )
                }
                TeddTokenProgress(
                    value = st.tokensToTarget,
                    accent = st.tier?.let { tierColor(it) } ?: TeddTokens.InkMuted,
                    showLabel = true,
                )
            }
        }

        SkillSearchSection(st, model, compact)
        model.lastImport?.takeIf { it.skillId == st.skill.id }?.let { r ->
            Text(
                "Parcours créé : ${r.steps} étapes, ${r.objectives} objectifs, ${r.micros} micro, ${r.projects} projets et ${r.actions} actions.",
                style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 13.sp,
            )
        }
        ParcoursSection(st, model, compact = compact, onNavigate = onNavigate, onStartFocus = onStartFocus)
        val resources = rememberResourcesModel(model.resourceRepo, st.skill.id, org.teddapp.project.server.LocalServerSession.current)
        ResourcesSection(resources, compact = compact)
        model.convertingSkill?.takeIf { it.id == st.skill.id }?.let { skill ->
            ConvertToMindMapDialog(
                skill = skill,
                preview = model.convertPreview,
                onConfirm = { model.convertMindMap(skill) { id -> NavIntent.openMindMap(id); onNavigate(Section.MindMaps) } },
                onClose = { model.convertingSkill = null },
            )
        }

        val cartes = model.mindMapsOf(st.skill.id)
        if (cartes.isNotEmpty()) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text("CARTES HEURISTIQUES", style = TeddType.Overline, color = TeddTokens.InkMuted)
                cartes.forEach { carte ->
                    TeddCard(
                        modifier = Modifier.fillMaxWidth().revealed(model, "map:${carte.id}").clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                            NavIntent.openMindMap(carte.id); onNavigate(Section.MindMaps)
                        },
                        padding = PaddingValues(14.dp),
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                            TeddIcon(name = "mindmap", size = 16.dp, color = TeddTokens.ModNotes)
                            Text(carte.title, style = TeddType.BodyStrong, color = TeddTokens.Ink, modifier = Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
                            Text("Ouvrir →", style = TeddType.Caption, color = TeddTokens.InkMuted)
                        }
                    }
                }
            }
        }

        val parcoursProjectIds = model.steps.filter { it.skill_id == st.skill.id }.mapNotNull { it.project_id }.toHashSet()
        val looseProjects = st.projects.filter { it.id !in parcoursProjectIds }
        if (looseProjects.isNotEmpty()) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text("PROJETS RATTACHÉS", style = TeddType.Overline, color = TeddTokens.InkMuted)
                looseProjects.forEach { proj ->
                    var open by remember(proj.id) { mutableStateOf(false) }
                    TeddCard(modifier = Modifier.fillMaxWidth(), padding = PaddingValues(14.dp)) {
                        val (done, total) = model.projectProgress(proj.id)
                        val finished = total > 0 && done == total
                        // La ligne déplie les actions, cochables ici ; « ouvrir » mène au projet.
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            modifier = Modifier.fillMaxWidth().clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { open = !open },
                        ) {
                            TeddIcon(name = if (finished) "check" else proj.icon, size = 13.dp, color = if (finished) TeddTokens.Success else TeddTokens.InkMuted, strokeWidth = 2.dp)
                            Text(
                                proj.name,
                                color = if (finished) TeddTokens.InkMuted else TeddTokens.Ink,
                                fontSize = 13.5.sp, fontWeight = FontWeight.Medium, modifier = Modifier.weight(1f),
                            )
                            // Où en est le projet : terminé, ou tant d'actions faites sur tant.
                            Text(
                                when {
                                    total == 0 -> "aucune action"
                                    finished -> "terminé"
                                    else -> "$done / $total"
                                },
                                style = TeddType.Mono,
                                color = if (finished) TeddTokens.Success else TeddTokens.InkMuted,
                                fontSize = 11.5.sp,
                            )
                            DisableSelection {
                                Text(
                                    "ouvrir",
                                    style = TeddType.Overline,
                                    color = TeddTokens.LimeStrong,
                                    modifier = Modifier.clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                                        NavIntent.openProject(proj.id)
                                        onNavigate(Section.Projects)
                                    },
                                )
                            }
                        }
                        if (open) {
                            Column(verticalArrangement = Arrangement.spacedBy(10.dp), modifier = Modifier.padding(top = 10.dp)) {
                                model.tasksOf(proj.id).filter { it.parent_kind == "project" }.forEach { a -> ActionRow(a, model, onStartFocus) }
                            }
                        }
                    }
                }
            }
        }

        val looseObjectives = st.objectives.filter { it.step_id == null }
        if (looseObjectives.isNotEmpty()) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text("OBJECTIFS RATTACHÉS", style = TeddType.Overline, color = TeddTokens.InkMuted)
                looseObjectives.forEach { obj ->
                    // Le même bloc que dans le parcours : dépliable, micro cochables, lien « ouvrir ».
                    TeddCard(modifier = Modifier.fillMaxWidth(), padding = PaddingValues(14.dp)) {
                        ObjectiveBlock(obj, model, onNavigate, onStartFocus)
                    }
                }
            }
        }

        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Text("CE QUE J'AI COMPRIS", style = TeddType.Overline, color = TeddTokens.InkMuted)
            if (st.explanations.isEmpty()) {
                Text(
                    "Aucune explication écrite pour l'instant. À la fin d'une course de focus, une phrase suffit : c'est elle qui révèle ce qui manque.",
                    style = TeddType.BodyLg,
                    color = TeddTokens.InkMuted,
                    fontSize = 14.sp,
                )
            } else {
                // La recherche peut viser une note au-delà des premières : elles sont toutes là, les plus récentes d'abord.
                val shown = if (model.revealed?.startsWith("session:") == true) st.explanations else st.explanations.take(if (compact) 5 else 20)
                shown.forEach { s ->
                    Column(modifier = Modifier.fillMaxWidth().revealed(model, "session:${s.id}").padding(horizontal = 4.dp)) {
                        Text(
                            text = "${TeddDates.formatShort(TeddDates.isoFromDays((s.started_at / 86_400_000L).toInt())) ?: ""} · ${s.focus_minutes} min",
                            style = TeddType.Mono,
                            color = TeddTokens.InkMuted,
                            fontSize = 11.5.sp,
                        )
                        Text(s.note.orEmpty(), style = TeddType.BodyLg, color = TeddTokens.Ink, fontSize = 15.sp)
                    }
                }
            }
        }

        val isLanguage = looksLikeLanguage(st.skill.name, st.skill.intention)
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            if (isLanguage) {
                Text("PRODUIRE DANS LA LANGUE", style = TeddType.Overline, color = TeddTokens.InkMuted)
                Text(
                    text = "Une phrase produite sans aide vaut une heure de révision passive. " +
                        "Écris-la, puis dis ce que tu voulais dire.",
                    style = TeddType.BodyLg,
                    color = TeddTokens.InkMuted,
                    fontSize = 14.sp,
                )
                DisableSelection {
                    TeddButton(
                        text = "Écrire une tentative",
                        onClick = { model.languageFor = st.skill },
                        kind = TeddButtonKind.Secondary,
                        size = TeddButtonSize.Md,
                    )
                }
            } else {
                DisableSelection {
                    TeddButton(
                        text = "C'est une langue ? Écrire une tentative",
                        onClick = { model.languageFor = st.skill },
                        kind = TeddButtonKind.Ghost,
                        size = TeddButtonSize.Sm,
                    )
                }
            }
            model.lastAttempt?.let {
                Text("Dernière tentative enregistrée : « $it »", color = TeddTokens.InkMuted, fontSize = 12.sp)
            }
        }

        model.languageFor?.takeIf { it.id == st.skill.id }?.let { skill ->
            // Le dialogue est une autre surface : il ne participe pas à la sélection de la fiche.
            DisableSelection {
                LanguageAttemptDialog(
                    initialLanguage = skill.name,
                    onSubmit = { lang, attempt, intention -> model.recordLanguageAttempt(lang, attempt, intention) },
                    onClose = { model.languageFor = null },
                )
            }
        }
    }
}

/** La liste des compétences : 340 dp par défaut, de 260 à 520. */
private val SKILLS_PANE = org.teddapp.project.ui.PaneSpec("skills.list", default = 340.dp, min = 260.dp, max = 520.dp, collapsed = 60.dp)

/** Langues reconnues au nom de la compétence : suffit pour proposer l'exercice au bon endroit. */
private val LANGUAGE_WORDS = listOf(
    "anglais", "english", "espagnol", "spanish", "allemand", "deutsch", "italien", "portugais",
    "chinois", "mandarin", "japonais", "coréen", "coreen", "arabe", "russe", "néerlandais",
    "neerlandais", "langue", "wolof", "bambara", "swahili", "hindi", "turc", "grec", "latin",
)

/** Vrai si la compétence ressemble à une langue, d'après son nom ou son intention. */
internal fun looksLikeLanguage(name: String, intention: String?): Boolean {
    val hay = (name + " " + intention.orEmpty()).lowercase()
    return LANGUAGE_WORDS.any { hay.contains(it) }
}

/**
 * Tentative de langue : on écrit ce qu'on a produit, puis ce qu'on voulait dire. Le noyau
 * garde l'écart entre les deux ; il ne corrige pas la phrase, il apprend de la distance.
 */
@Composable
private fun LanguageAttemptDialog(
    initialLanguage: String,
    onSubmit: (language: String, attempt: String, intention: String) -> Unit,
    onClose: () -> Unit,
) {
    var language by remember(initialLanguage) { mutableStateOf(initialLanguage) }
    var attempt by remember(initialLanguage) { mutableStateOf("") }
    var intention by remember(initialLanguage) { mutableStateOf("") }

    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .width(460.dp)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Text("Tentative de langue", style = TeddType.Title2, color = TeddTokens.Ink)
            Text(
                text = "Écris d'abord dans la langue, sans dictionnaire. Puis dis en français ce que tu voulais dire. " +
                    "Rien n'est corrigé : c'est l'écart entre les deux qui est utile.",
                style = TeddType.BodyLg,
                color = TeddTokens.InkMuted,
                fontSize = 14.sp,
            )
            EditorField("Langue", language, "Anglais") { language = it }
            EditorField("Ce que j'ai produit", attempt, "I have been working on this since two weeks.", minHeight = 88.dp) { attempt = it }
            EditorField("Ce que je voulais dire", intention, "Je travaille là-dessus depuis deux semaines.", minHeight = 64.dp) { intention = it }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                TeddButton(
                    text = "Enregistrer",
                    onClick = { onSubmit(language, attempt, intention) },
                    kind = TeddButtonKind.Primary,
                    size = TeddButtonSize.Md,
                    modifier = Modifier.weight(1f),
                )
                TeddButton(text = "Annuler", onClick = onClose, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Md)
            }
        }
    }
}

/** Rien ne correspond : on le dit, plutôt que de laisser une colonne vide. */
@Composable
private fun SkillsNoMatch(model: SkillsModel) {
    Text(
        text = when {
            model.tagFilter != null -> "Aucune compétence en jeu sous « ${model.tagFilter} »."
            model.search.isNotBlank() -> "Aucune compétence ne correspond à « ${model.search.trim()} »."
            else -> "Toutes les compétences sont archivées."
        },
        color = TeddTokens.InkMuted,
        style = TeddType.BodyLg,
        fontSize = 14.sp,
        modifier = Modifier.padding(vertical = 8.dp),
    )
}

@Composable
private fun DetailNumber(label: String, value: String, compact: Boolean = true) {
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(
            text = value,
            style = TeddType.Numeral.copy(
                fontSize = if (compact) 22.sp else 30.sp,
                lineHeight = if (compact) 26.sp else 34.sp,
            ),
            color = TeddTokens.Ink,
        )
        Text(label.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted)
    }
}

@Composable
private fun UnclassifiedNote(model: SkillsModel) {
    val m = model.unclassifiedMinutes
    if (m <= 0) return
    Text(
        text = "${formatHours(m)} de focus ne sont rattachées à aucune compétence. Rattache un objectif à une compétence, ou choisis-en une en fin de course.",
        color = TeddTokens.InkMuted,
        fontSize = 12.sp,
        modifier = Modifier.padding(top = 4.dp),
    )
}

@Composable
private fun SkillsEmpty(onCreate: () -> Unit, onLoad: @Composable () -> Unit = {}, modifier: Modifier = Modifier) {
    Column(
        modifier = modifier.fillMaxSize().padding(32.dp),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text("Aucune compétence déclarée", style = TeddType.Title2, color = TeddTokens.Ink)
        Text(
            text = "Une compétence, c'est ce que tu veux savoir faire : l'anglais, Kafka, la guitare. " +
                "Le temps passé en focus s'y accumule, et les paliers arrivent à 20, 100 puis 1 000 heures.",
            style = TeddType.BodyLg,
            color = TeddTokens.InkMuted,
            modifier = Modifier.padding(top = 10.dp, bottom = 18.dp).width(460.dp),
        )
        TeddButton(text = "Déclarer une compétence", onClick = onCreate, kind = TeddButtonKind.Primary, size = TeddButtonSize.Lg)
        Text(
            "Ou apporte un parcours complet : un fichier JSON crée la compétence, ses modules, ses objectifs et ses projets.",
            style = TeddType.BodyLg, color = TeddTokens.InkMuted, fontSize = 13.sp,
            modifier = Modifier.padding(top = 22.dp, bottom = 10.dp).width(460.dp),
        )
        onLoad()
    }
}

/** Création et modification d'une compétence : nom, intention, cible, couleur, icône. */
@Composable
private fun SkillEditor(
    existing: Skill?,
    families: List<String>,
    onSave: (name: String, intention: String, color: String, icon: String, targetHours: Long, tags: String?, deadline: String?) -> Unit,
    onDelete: (() -> Unit)?,
    onClose: () -> Unit,
) {
    var name by remember(existing) { mutableStateOf(existing?.name ?: "") }
    var deadline by remember(existing) { mutableStateOf(existing?.deadline.orEmpty()) }
    var intention by remember(existing) { mutableStateOf(existing?.intention ?: "") }
    var color by remember(existing) { mutableStateOf(existing?.color ?: "#22508A") }
    var icon by remember(existing) { mutableStateOf(existing?.icon ?: "book") }
    var target by remember(existing) { mutableStateOf(existing?.target_hours ?: 1000L) }
    var tags by remember(existing) { mutableStateOf(existing?.tags.orEmpty()) }
    val palette = listOf("#22508A", "#2D7A4F", "#9A6B3A", "#6F7D8C", "#B8892C", "#0F7C8C")
    val icons = listOf("book", "focus", "target", "spark", "grid", "flag")

    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .width(460.dp)
                .clip(RoundedCornerShape(TeddTokens.RXl))
                .background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RXl))
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Text(if (existing == null) "Nouvelle compétence" else "Modifier la compétence", style = TeddType.Title2, color = TeddTokens.Ink)
            EditorField("Nom", name, "Anglais, Kafka, guitare…") { name = it }
            EditorField("Intention", intention, "Tenir une conversation de 30 minutes.", minHeight = 64.dp) { intention = it }
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                EditorField("Famille, puis étiquettes", tags, "données, ml — la première range la compétence dans la liste") { tags = it }
                // Les familles existantes se choisissent d'un tap ; une nouvelle s'écrit dans le champ,
                // en premier, et existe dès qu'une compétence la porte.
                if (families.isNotEmpty()) {
                    val current = parseTags(tags)
                    FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        families.forEach { fam ->
                            val on = current.firstOrNull() == fam
                            Box(
                                modifier = Modifier
                                    .clip(RoundedCornerShape(TeddTokens.RFull))
                                    .background(if (on) TeddTokens.LimeStrong else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
                                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) {
                                        // La famille prend la première place et remplace l'ancienne ; les autres étiquettes suivent.
                                        val oldFirst = current.firstOrNull()
                                        val keep = current.filter { it != fam && !(it == oldFirst && oldFirst in families) }
                                        tags = (listOf(fam) + keep).joinToString(", ")
                                    }
                                    .padding(horizontal = 10.dp, vertical = 5.dp),
                            ) {
                                Text(fam, color = if (on) TeddTokens.OnAction else TeddTokens.InkMuted, fontSize = 11.5.sp, fontWeight = FontWeight.SemiBold)
                            }
                        }
                    }
                }
            }
            Column {
                Text("CIBLE · ARGENT À LA MOITIÉ, OR À 95 %", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
                Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    listOf(20L, 100L, 300L, 1000L).forEach { h ->
                        val on = target == h
                        Box(
                            modifier = Modifier
                                .clip(RoundedCornerShape(TeddTokens.RFull))
                                .background(if (on) TeddTokens.LimeStrong else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RFull))
                                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { target = h }
                                .padding(horizontal = 12.dp, vertical = 6.dp),
                        ) {
                            Text("$h h", color = if (on) TeddTokens.OnAction else TeddTokens.Ink, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                        }
                    }
                }
            }
            // La date butoir : le jour où l'on veut avoir atteint la cible. Rien d'obligatoire.
            TeddDateField(
                label = "Date butoir",
                value = deadline,
                onValueChange = { deadline = it },
                placeholder = "Aucune date",
                modifier = Modifier.fillMaxWidth(),
            )
            Row(horizontalArrangement = Arrangement.spacedBy(18.dp)) {
                Column {
                    Text("COULEUR", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
                    Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        palette.forEach { hex ->
                            val c = runCatching { Color(("FF" + hex.trimStart('#')).toLong(16)) }.getOrElse { TeddTokens.LimeStrong }
                            Box(
                                modifier = Modifier
                                    .size(26.dp)
                                    .clip(RoundedCornerShape(TeddTokens.RSm))
                                    .background(c, RoundedCornerShape(TeddTokens.RSm))
                                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { color = hex },
                                contentAlignment = Alignment.Center,
                            ) { if (color == hex) TeddIcon(name = "check", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 2.6.dp) }
                        }
                    }
                }
                Column {
                    Text("ICÔNE", style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
                    Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        icons.forEach { name2 ->
                            val on = icon == name2
                            Box(
                                modifier = Modifier
                                    .size(26.dp)
                                    .clip(RoundedCornerShape(TeddTokens.RSm))
                                    .background(if (on) TeddTokens.Ink else TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { icon = name2 },
                                contentAlignment = Alignment.Center,
                            ) { TeddIcon(name = name2, size = 13.dp, color = if (on) TeddTokens.Bg else TeddTokens.InkMuted, strokeWidth = 2.dp) }
                        }
                    }
                }
            }
            Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                if (onDelete != null) {
                    TeddButton(text = "Supprimer", onClick = onDelete, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Md)
                }
                Spacer(modifier = Modifier.weight(1f))
                TeddButton(text = "Annuler", onClick = onClose, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md)
                TeddButton(
                    text = if (existing == null) "Créer" else "Enregistrer",
                    onClick = { onSave(name, intention, color, icon, target, joinTags(parseTags(tags)), deadline.takeIf { it.isNotBlank() }) },
                    kind = TeddButtonKind.Primary,
                    size = TeddButtonSize.Md,
                    enabled = name.isNotBlank(),
                )
            }
        }
    }
}

/** La date butoir, du ton de son échéance : discrète loin, marquée à moins de quinze jours, en alerte dépassée. */
@Composable
internal fun DeadlineText(label: DeadlineLabel, overline: Boolean = false) {
    val color = when (label.tone) {
        DeadlineTone.Calm -> TeddTokens.InkSubtle
        DeadlineTone.Soon -> TeddTokens.Ink
        DeadlineTone.Past -> TeddTokens.Danger
    }
    Text(
        text = if (overline) label.text.uppercase() else label.text,
        style = if (overline) TeddType.Overline else TeddType.Mono,
        color = color,
        fontSize = if (overline) TeddType.Overline.fontSize else 11.sp,
    )
}

@Composable
private fun EditorField(label: String, value: String, placeholder: String, minHeight: androidx.compose.ui.unit.Dp = 44.dp, onChange: (String) -> Unit) {
    Column {
        Text(label.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = Modifier.padding(bottom = 6.dp))
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = minHeight)
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                .padding(horizontal = 12.dp, vertical = 12.dp),
            contentAlignment = Alignment.CenterStart,
        ) {
            BasicTextField(
                value = value,
                onValueChange = onChange,
                singleLine = minHeight <= 44.dp,
                textStyle = TeddType.Body.copy(color = TeddTokens.Ink, fontSize = 14.sp),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                modifier = Modifier.fillMaxWidth(),
                decorationBox = { inner ->
                    if (value.isEmpty()) Text(placeholder, color = TeddTokens.InkSubtle, fontSize = 14.sp)
                    inner()
                },
            )
        }
    }
}


/** Ce que la conversion d'une compétence en carte heuristique va créer, avant de le faire. */
@Composable
private fun ConvertToMindMapDialog(skill: Skill, preview: Map<String, Int>, onConfirm: () -> Unit, onClose: () -> Unit) {
    // Le dialogue se retire à l'image suivante : le retirer dans le clic gèle le navigateur.
    val confirm = org.teddapp.project.ui.rememberAfterFrame(onConfirm)
    val close = org.teddapp.project.ui.rememberAfterFrame(onClose)
    val labels = listOf(
        "parcours_step" to ("étape" to "étapes"),
        "objective" to ("objectif" to "objectifs"),
        "milestone" to ("jalon" to "jalons"),
        "task" to ("tâche" to "tâches"),
        "project" to ("projet" to "projets"),
        "competence" to ("compétence du recueil" to "compétences du recueil"),
        "competence_criterion" to ("critère" to "critères"),
        "flashcard" to ("carte mémoire" to "cartes mémoire"),
    )
    val parts = labels.mapNotNull { (k, l) -> preview[k]?.takeIf { it > 0 }?.let { n -> "$n ${if (n > 1) l.second else l.first}" } }
    // La fiche vit dans un SelectionContainer ; un dialogue composé dedans y inscrit ses
    // textes, et le gestionnaire de sélection compare alors des coordonnées de deux
    // hiérarchies (la fenêtre et le dialogue) : plantage dans le navigateur au premier
    // clic dans le dialogue. DisableSelection coupe cette inscription.
    androidx.compose.ui.window.Dialog(onDismissRequest = close, properties = androidx.compose.ui.window.DialogProperties(usePlatformDefaultWidth = false)) {
        DisableSelection {
        Column(
            modifier = Modifier
                .width(440.dp)
                .background(TeddTokens.SurfaceOverlay, RoundedCornerShape(TeddTokens.RLg))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RLg))
                .padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            Text("Convertir « ${skill.name} » en carte heuristique", style = TeddType.Title2, color = TeddTokens.Ink)
            Text(
                if (parts.isEmpty()) "La compétence devient le sujet central ; tu ajoutes ensuite les branches."
                else "La compétence devient le sujet central, avec ses branches : ${parts.joinToString(", ")}. Chaque nœud garde un lien vers sa source, et la carte pourra se mettre à jour plus tard sans écraser ce que tu modifies.",
                style = TeddType.Body, color = TeddTokens.InkMuted,
            )
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                TeddButton(text = "Convertir", onClick = confirm, kind = TeddButtonKind.Primary, modifier = Modifier.weight(1f), icon = "mindmap")
                TeddButton(text = "Annuler", onClick = close, kind = TeddButtonKind.Ghost)
            }
        }
        }
    }
}
